import React, { useRef } from 'react';
import loadable from '@loadable/component';
import { createContent } from '@plone/volto/actions/content/content';
import { searchContent } from '@plone/volto/actions/search/search';
import useLinkEditor from '@plone/volto/components/manage/AnchorPlugin/useLinkEditor';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';
import { urlValidator } from '@plone/volto/helpers/FormValidation/validators';
import {
  flattenToAppURL,
  getBaseUrl,
  isInternalURL,
  normalizeUrl,
  removeProtocol,
} from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import ImageUploadModal from 'govbr.frontend.policy/components/Widgets/ImageUploadModal';
import { defineMessages, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { compose } from 'redux';
import { Button, Dimmer, Message } from 'semantic-ui-react';

import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import linkSVG from '@plone/volto/icons/link.svg';
import navTreeSVG from '@plone/volto/icons/nav.svg';
import uploadSVG from '@plone/volto/icons/upload.svg';

const Dropzone = loadable(() => import('react-dropzone'));

export const ImageToolbar = ({ className, data, id, onChange, selected }) => (
  <div className="image-upload-widget-toolbar">
    <Button.Group>
      <Button icon basic onClick={() => onChange(id, null)}>
        <Icon className="circled" name={clearSVG} size="24px" color="#e40166" />
      </Button>
    </Button.Group>
  </div>
);

const messages = defineMessages({
  addImage: {
    id: 'Browse the site, drop an image, or type a URL',
    defaultMessage: 'Browse the site, drop an image, or use a URL',
  },
  pickAnImage: {
    id: 'pickAnImage',
    defaultMessage: 'Pick an existing image',
  },
  uploadAnImage: {
    id: 'uploadAnImage',
    defaultMessage: 'Upload an image from your computer',
  },
  linkAnImage: {
    id: 'linkAnImage',
    defaultMessage: 'Enter a URL to an image',
  },
  Error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  imageUploadErrorMessage: {
    id: 'imageUploadErrorMessage',
    defaultMessage: 'Please upload an image instead.',
  },
  externalURLsNotAllowed: {
    id: 'externalURLsNotAllowed',
    defaultMessage: 'External URLs are not allowed in this field.',
  },
  internalImageNotFoundErrorMessage: {
    id: 'internalImageNotFoundErrorMessage',
    defaultMessage: 'No image was found in the internal path you provided.',
  },
});

const UnconnectedImageInput = (props) => {
  const {
    id,
    onChange,
    onFocus,
    openObjectBrowser,
    value,
    imageSize = 'teaser',
    selected = true,
    hideLinkPicker = false,
    hideObjectBrowserPicker = false,
    restrictFileUpload = false,
    objectBrowserPickerType = 'image',
    description,
    placeholderLinkInput = '',
    onSelectItem,
  } = props;
  const imageValue = value?.[0]?.['@id'] || value?.['@id'] || value;

  const intl = useIntl();
  const linkEditor = useLinkEditor();
  const location = useLocation();
  const contextUrl = location.pathname;

  const [dragging, setDragging] = React.useState(false);

  // CUSTOMIZAÇÃO: Estado para controlar o modal e arquivo selecionado
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);

  const imageUploadInputRef = useRef(null);

  const isRelationChoice = props.factory === 'Relation Choice';

  // CUSTOMIZAÇÃO: Modificar handleUpload para abrir o modal ao invés de fazer upload direto
  const handleUpload = React.useCallback(
    (eventOrFile) => {
      if (restrictFileUpload === true) return;
      eventOrFile.target && eventOrFile.stopPropagation();

      const file = eventOrFile.target
        ? eventOrFile.target.files[0]
        : eventOrFile[0];

      if (!validateFileUploadSize(file, intl.formatMessage)) {
        return;
      }

      // CUSTOMIZAÇÃO: Abrir modal ao invés de fazer upload direto
      setSelectedFile(file);
      setModalOpen(true);
    },
    [restrictFileUpload, intl.formatMessage],
  );

  // CUSTOMIZAÇÃO: Handler para quando o modal fechar com sucesso
  const handleModalSuccess = React.useCallback(
    (imageData) => {
      setModalOpen(false);
      setSelectedFile(null);

      // Atualizar o widget com a imagem criada
      if (imageData?.['@id']) {
        if (isRelationChoice) {
          onChange(id, imageData, {
            image_field: 'image',
            image_scales: imageData.image ? { image: [imageData.image] } : {},
            alt: imageData.alt || '',
            caption: imageData.image_caption || '',
          });
        } else {
          onChange(id, imageData['@id'], {
            image_field: 'image',
            image_scales: imageData.image ? { image: [imageData.image] } : {},
            alt: imageData.alt || '',
            caption: imageData.image_caption || '',
          });
        }
      }
    },
    [id, isRelationChoice, onChange],
  );

  // CUSTOMIZAÇÃO: Handler para quando o modal fechar sem sucesso
  const handleModalClose = React.useCallback(() => {
    setModalOpen(false);
    setSelectedFile(null);
  }, []);

  const onDragEnter = React.useCallback(() => {
    if (restrictFileUpload === false) setDragging(true);
  }, [restrictFileUpload]);

  const onDragLeave = React.useCallback(() => setDragging(false), []);

  const validateManualLink = React.useCallback(
    (url) => {
      if (!url.startsWith('/')) {
        const error = urlValidator({
          value: url,
          formatMessage: intl.formatMessage,
        });
        return !Boolean(error);
      } else {
        return isInternalURL(url);
      }
    },
    [intl.formatMessage],
  );

  const onSubmitURL = React.useCallback(
    (url) => {
      if (validateManualLink(url)) {
        if (isInternalURL(url)) {
          props
            .searchContent(
              '/',
              {
                portal_type: config.settings.imageObjects,
                'path.query': flattenToAppURL(url),
                'path.depth': '0',
                sort_on: 'getObjPositionInParent',
                metadata_fields: '_all',
                b_size: 1000,
              },
              `${props.block}-${props.mode}`,
            )
            .then((resp) => {
              if (resp.items?.length > 0) {
                onChange(props.id, resp.items[0], {});
              } else {
                toast.error(
                  <Toast
                    error
                    title={intl.formatMessage(messages.Error)}
                    content={intl.formatMessage(
                      messages.internalImageNotFoundErrorMessage,
                    )}
                  />,
                );
              }
            });
        } else {
          if (isRelationChoice) {
            toast.error(
              <Toast
                error
                title={intl.formatMessage(messages.Error)}
                content={intl.formatMessage(messages.imageUploadErrorMessage)}
              />,
            );
          } else {
            onChange(props.id, [
              {
                '@id': normalizeUrl(url),
                title: removeProtocol(url),
              },
            ]);
          }
        }
      }
    },
    [validateManualLink, props, intl, isRelationChoice, onChange],
  );

  return (
    <>
      {imageValue ? (
        <div
          className="image-upload-widget-image"
          onClick={onFocus}
          onKeyDown={onFocus}
          role="toolbar"
        >
          {selected && <ImageToolbar {...props} />}
          {isRelationChoice ? (
            <Image
              item={value}
              width="fit-content"
              height="auto"
              loading="lazy"
              alt={value?.alt || ''}
            />
          ) : (
            <Image
              className={props.className}
              src={
                isInternalURL(imageValue)
                  ? `${flattenToAppURL(imageValue)}/@@images/image/${imageSize}`
                  : imageValue
              }
              alt={value?.alt || ''}
            />
          )}
        </div>
      ) : (
        <div
          className="image-upload-widget"
          onClick={onFocus}
          onKeyDown={onFocus}
          role="toolbar"
        >
          <Dropzone
            noClick
            accept="image/*"
            onDrop={(acceptedFiles) => {
              setDragging(false);
              if (acceptedFiles.length > 0) {
                handleUpload(acceptedFiles);
              } else {
                toast.error(
                  <Toast
                    error
                    title={intl.formatMessage(messages.Error)}
                    content={intl.formatMessage(
                      messages.imageUploadErrorMessage,
                    )}
                  />,
                );
              }
            }}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            className="dropzone"
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <Message>
                  {dragging && <Dimmer active />}
                  <Image src={imageBlockSVG} alt="" className="placeholder" />
                  <p>{description || intl.formatMessage(messages.addImage)}</p>
                  <div className="toolbar-wrapper">
                    <div className="toolbar-inner" ref={linkEditor.anchorNode}>
                      {hideObjectBrowserPicker === false && (
                        <Button.Group>
                          <Button
                            aria-label={intl.formatMessage(
                              messages.pickAnImage,
                            )}
                            icon
                            basic
                            onClick={(e) => {
                              onFocus && onFocus();
                              e.preventDefault();
                              openObjectBrowser({
                                mode: objectBrowserPickerType,
                                onSelectItem: isRelationChoice
                                  ? (url, item) => {
                                      onChange(props.id, item);
                                    }
                                  : onSelectItem
                                    ? onSelectItem
                                    : (
                                        url,
                                        { title, image_field, image_scales },
                                      ) => {
                                        onChange(
                                          props.id,
                                          flattenToAppURL(url),
                                          {
                                            title,
                                            image_field,
                                            image_scales,
                                          },
                                        );
                                      },
                                currentPath: contextUrl,
                              });
                            }}
                            type="button"
                          >
                            <Icon name={navTreeSVG} size="24px" />
                          </Button>
                        </Button.Group>
                      )}
                      {restrictFileUpload === false && (
                        <Button.Group>
                          <Button
                            aria-label={intl.formatMessage(
                              messages.uploadAnImage,
                            )}
                            icon
                            basic
                            compact
                            onClick={() => {
                              imageUploadInputRef.current.click();
                            }}
                            type="button"
                          >
                            <Icon name={uploadSVG} size="24px" />
                          </Button>
                          <input
                            {...getInputProps({
                              type: 'file',
                              ref: imageUploadInputRef,
                              onChange: handleUpload,
                              style: { display: 'none' },
                              accept: 'image/*',
                            })}
                          />
                        </Button.Group>
                      )}

                      {hideLinkPicker === false && (
                        <Button.Group>
                          <Button
                            icon
                            basic
                            aria-label={intl.formatMessage(
                              messages.linkAnImage,
                            )}
                            onClick={(e) => {
                              !props.selected && onFocus && onFocus();
                              linkEditor.show();
                            }}
                            type="button"
                          >
                            <Icon name={linkSVG} circled size="24px" />
                          </Button>
                        </Button.Group>
                      )}
                    </div>
                    {linkEditor.anchorNode && (
                      <linkEditor.LinkEditor
                        value={imageValue}
                        placeholder={
                          placeholderLinkInput ||
                          intl.formatMessage(messages.linkAnImage)
                        }
                        objectBrowserPickerType={objectBrowserPickerType}
                        onChange={(_, e) => {
                          onSubmitURL(e);
                        }}
                        id={id}
                      />
                    )}
                  </div>
                </Message>
              </div>
            )}
          </Dropzone>
        </div>
      )}

      {/* CUSTOMIZAÇÃO: Renderizar o modal quando há um arquivo selecionado */}
      {selectedFile && (
        <ImageUploadModal
          open={modalOpen}
          file={selectedFile}
          path={getBaseUrl(contextUrl)}
          uploadContent={createContent}
          block={props.block}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export const ImageInput = compose(
  withObjectBrowser,
  connect(
    (state, ownProps) => {
      const requestId = `image-upload-${ownProps.id}`;
      return {
        request: state.content.subrequests[ownProps.block || requestId] || {},
        content: state.content.subrequests[ownProps.block || requestId]?.data,
      };
    },
    { createContent, searchContent },
  ),
)(UnconnectedImageInput);

const ImageUploadWidget = (props) => {
  const { fieldSet, id, title } = props;
  return (
    <FormFieldWrapper
      {...props}
      columns={1}
      className="block image-upload-widget"
    >
      <div className="wrapper">
        <label
          id={`fieldset-${fieldSet}-field-label-${id}`}
          htmlFor={`field-${id}`}
        >
          {title}
        </label>
      </div>
      <ImageInput {...props} />
    </FormFieldWrapper>
  );
};

export default ImageUploadWidget;
